import { createContext, useContext } from 'react'

// Vendor-agnostic analytics contract. Call-sites depend on the
// `Analytics` type, never on a specific SDK. The web portal's adapter
// wraps posthog-js; the future mobile adapter will wrap
// posthog-react-native. Both produce values of this shape.
//
// Method names follow the Segment / Mixpanel / Amplitude convention
// (`track`, not PostHog's `capture`) — the adapter translates.

export type Analytics = {
  track: (event: string, props?: Record<string, unknown>) => void
  identify: (userId: string, traits?: Record<string, unknown>) => void
  reset: () => void
}

// 'website' for the marketing landing site (Eleventy build); 'webapp'
// for the portal SPA bundle; 'ios' / 'android' for the React Native
// bundles. Open set; extend as new clients ship. Registered as a
// PostHog super-property so every event auto-carries the surface that
// emitted it.
export type AnalyticsClient = 'website' | 'webapp' | 'ios' | 'android'

// 'preview' = PR preview deploys; 'release' = main-branch deploys.
export type AnalyticsDeployMode = 'preview' | 'release'

// Input shape consumed by every PostHog adapter (web today, mobile
// next). Each runtime's `initAnalytics` populates this from its own
// env-var prefix (VITE_* on web, EXPO_PUBLIC_* on mobile) and pins
// `client` to its runtime.
export type AnalyticsConfig = {
  apiKey: string | undefined
  apiHost: string | undefined
  deployMode: AnalyticsDeployMode | undefined
  client: AnalyticsClient
}

// Lowest-common-denominator surface of a PostHog SDK client — what
// both posthog-js and posthog-react-native expose with identical
// signatures. Used by `analyticsFromPostHog` to bridge from either
// SDK to our vendor-agnostic Analytics type.
type PostHogLikeClient = {
  capture: (event: string, props?: Record<string, unknown>) => void
  identify: (userId: string, traits?: Record<string, unknown>) => void
  reset: () => void
}

// Translates a PostHog SDK client into our vendor-agnostic Analytics
// shape. Both PostHog SDKs (web + RN) expose the same three method
// signatures, so this lives in core to avoid each adapter
// re-implementing the same delegation.
export function analyticsFromPostHog(client: PostHogLikeClient): Analytics {
  return {
    track: (event, props) => client.capture(event, props),
    identify: (userId, traits) => client.identify(userId, traits),
    reset: () => client.reset(),
  }
}

export const noopAnalytics: Analytics = {
  track: () => {},
  identify: () => {},
  reset: () => {},
}

// Sentinel distinguishes "outside provider" from the no-op used when
// PostHog is unconfigured. Shared across web and mobile providers.
export const NO_PROVIDER: unique symbol = Symbol()

export const AnalyticsContext = createContext<Analytics | typeof NO_PROVIDER>(
  NO_PROVIDER
)

// Always returns a non-null Analytics. Throws if called outside
// AnalyticsProvider — that's a coding mistake, not a runtime condition.
export function useAnalytics(): Analytics {
  const value = useContext(AnalyticsContext)
  if (value === NO_PROVIDER) {
    throw new Error('useAnalytics must be used within AnalyticsProvider')
  }
  return value
}
